import { Component, Input, OnDestroy, OnInit, Renderer2, ViewChild, ElementRef, ViewChildren, QueryList } from '@angular/core';
import { Router } from '@angular/router';

import { combineLatest, Observable, Subscription } from 'rxjs';
import * as uuid from 'uuid';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { ChartSetupOptionModel, reportChartOptions } from '../../../../Common/Dashboard/models/chart-setup-option.model';
import { AbstractPageBuilderService } from '../../../PageBuilder/services/abstract-page-builder.service';
import { CheckboxTypesEnum } from '../../../Forms/enums/checkbox-types.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { BlockTypeModel } from '../../../PageBuilder/models/block-type.model';
import { BlockModel } from '../../../PageBuilder/models/block.model';
import { ReportSeriesModel } from '../../models/report-series.model';
import { ReportModel } from '../../models/report.model';
import { DateQueryModel } from '../../../Forms/models/date-query.model';
import {
    ReportBlockTypeEnum,
    reportBlockTypesToSetupDialogAccentColorMapKeysMap,
    reportBlockTypeEnumToDefaultChartSpecificType,
    ReportChartInfo,
    reportChartInfoMap,
    ReportChartSourcesTypeEnum
} from '../../enums/report-block-type.enum';
import { ChartDisplayElementModel } from '../../models/chart-display-element.model';
import { ReportDateRangeModel } from '../../models/report-date-range.model';
import { GeographyTypesEnum } from '../../../../api/journodex/enum/geography-types.enum';
import { GoogleAnalyticsReportRequestTypeEnum } from '../../../../api/externalData/enums/google-analytics-report-request-type.enum';
import { SeriesTypeEnum } from '../../enums/series-type.enum';

import { ChartMetrics, ChartMetricsEnum } from '../../enums/chart-metrics.enum';
// eslint-disable-next-line max-len
import {
    coverageImpactTrendBlockType,
    coverageMetricBlockType,
    geographyBreakdownBlockType,
    referralTrendBlockType,
    sentimentBreakdownBlockType,
    sentimentBreakdownComparisonBlockType,
    sentimentTrendBlockType,
    topMentionsBlockType,
    topPostsBlockType
} from '../../models/reports-block-types.model';
import { GoogleAnalyticsReportTypesEnum } from '../../../../api/externalData/enums/google-analytics-report-types.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { ReportsService } from '../../services/reports.service';
import { ChartSetupSearchService } from '../../../Dashboard/services/chart-setup-search.service';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { FilterService } from '../../../../Common/Filter/services/abstract-filter.service';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { ChartSetupViewOptionsEnum } from '../../../../Common/Dashboard/enums/chart-setup-view-options.enum';
import { ScrollPanelComponent } from '../../../Basic/components/ScrollPanel/scroll-panel.component';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { ActionKeys } from '../../../../Common/Forms/enums/keys.enum';
import { TermTypesEnum } from '../../enums/term-types.enum';
import { ChartTimeIntervalsEnum, defaultChartTypesToTimeIntervalsMap } from '../../enums/chart-time-intervals.enum';
import { BlockFormComponent } from '../../../../Common/PageBuilder/components/BlockForm/block-form.component';
import { MentionCustomizationModel } from '../../../../Monitor/Shared/models/mention-customization.model';
import { MentionContentCustomizationEnum } from '../../../Mentions/enums/mention-content-customization.enum';
import { InsightsDisplayNameEnum } from '../../../../Common/Mentions/enums/insights-display-name.enum';
import { MentionMetadataTypesEnum } from '../../../../Monitor/Shared/enums/mention-metadata-types.enum';
import { SearchCriteriaSortFieldEnum } from '../../../../api/mentions/enum/search-criteria-sort-field.enum';
import { ReportsPageBuilderService } from '../../services/reports-page-builder.service';
import { ChartViewModesEnum } from '@Monitor/Shared/enums/chart-view-modes.enum';
import { CoverageMetricChartDisplayElement } from '@Common/Reports/models/coverage-metric-chart-display-element.model';
import { FilterTypeModel } from '../../../../Common/Filter/models/abstract-filter-type.model';
import { FilterCheckboxesComponent } from '../../../../Common/Filter/components/filterCheckboxes/filter-checkboxes.component';
import { ChartSetupFiltersEnum } from '../../../../Common/Dashboard/enums/chart-setup-filters.enum';
import { ChartDataTypesToChartMetricsMap, ChartDataTypesToSearchCriteriaSortFieldMap } from '../../../../Common/Dashboard/enums/chart-data-types.enum';
import { annotatableBlockTypes } from '../../../../Common/Reports/models/new-data-processing-workflow-block-types.model';


@Component({
    selector: 'agpr-chart-setup-dialog',
    templateUrl: './chart-setup-dialog.component.html',
    styleUrls: ['./chart-setup-dialog.component.scss'],
    providers: [
        {
            provide: 'SortOrderServiceInterface',
            useExisting: ChartSetupSearchService
        },
        {
            provide: FilterService,
            useExisting: ChartSetupSearchService
        },
        {
            provide: AbstractPageBuilderService,
            useExisting: ReportsPageBuilderService
        }
    ],
    standalone: false
})

export class ChartSetupDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() id: string = 'chartSetupDialog';
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() editState: boolean = false;
    @Input() globalDateQuery: DateQueryModel;
    @Input() hideDataConnectorCharts: boolean = false;
    @Input() hideMentionCharts: boolean = false;
    @Input() hideMetadataContainer: boolean = false;
    @Input() hideCoverageMetricChart: boolean = false;
    @Input() set selectedBlock(block: BlockModel) {
        if (block) {
            this.editState = true;
            this.pageBuilderService.setBlocks([block]);
            this.pageBuilderService.selectBlock(block);
        }
    }
    @Input() hidePreviewChartTooltip: boolean = false;
    @Input() displayEntityProfile: boolean = false;
    @Input() displaySourceAsLink: boolean = true;
    @Input() accentColor: string;
    @Input() overrideBlockTypeFields: ((type: BlockTypeModel) => void) | null = null;

    @ViewChild('blockForm') blockFormComponent: BlockFormComponent;
    @ViewChildren('chartFilter') chartFilterRefs: QueryList<FilterCheckboxesComponent>;

    readonly checkboxTypesEnum = CheckboxTypesEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    subscriptions: Subscription[] = [];
    blockSizeUpdateSubscription: Subscription;

    hideSlideOverlay: boolean = false;

    // For Chart Selection
    chartFilters: FilterTypeModel[] = [];
    anyFiltersSelected: boolean = false;
    showDescription: boolean = true;
    showGalleryView: boolean = true;
    charts: ChartSetupOptionModel[] = [];
    showDescriptionOption: DropdownOptionModel = new DropdownOptionModel('ChartSetupDialog.showDescription', ChartSetupViewOptionsEnum.showDescription);
    hideDescriptionOption: DropdownOptionModel = new DropdownOptionModel('ChartSetupDialog.hideDescription', ChartSetupViewOptionsEnum.hideDescription);
    chartViewOptions: DropdownOptionModel[] = [
        this.hideDescriptionOption
    ];
    selectedChartViewOption: DropdownOptionModel;
    // For Chart Settings
    selectedChartType: BlockTypeModel;
    originBlock: BlockModel;
    lastSelectedMetric: ChartMetricsEnum;
    blockClone: BlockModel;
    isAdding: boolean = true;
    chartIsValid: boolean = false;
    blockWidth: number;
    blockHeight: number;
    defaultTitles: string[] = [];
    oldTitle: string;
    private singleMentionSeries: ReportSeriesModel[] = [];
    private multiMentionSeries: ReportSeriesModel[] = [];
    private singleExternalSeries: ReportSeriesModel[] = [];
    private multiExternalSeries: ReportSeriesModel[] = [];
    private report: ReportModel;
    settingsScroll: boolean = false;
    chartChanged: boolean = false;

    viewModeOptions: DropdownOptionModel<ChartViewModesEnum>[] = [
        new DropdownOptionModel('', ChartViewModesEnum.GALLERY,'', 'fa-table-cells-large', 'Reports.chartSetupDialog.viewCards'),
        new DropdownOptionModel('', ChartViewModesEnum.LIST,'', 'fa-list', 'Reports.chartSetupDialog.viewLists')
    ];
    selectedViewMode: ChartViewModesEnum = ChartViewModesEnum.GALLERY;
    @ViewChild('scrollpanel') sp: ScrollPanelComponent;
    @ViewChild('settings') settingsDiv: ElementRef;

    constructor(
        public coreDialogService: CoreDialogService,
        protected renderer: Renderer2,
        public pageBuilderService: AbstractPageBuilderService,
        public localizationService: LocalizationService,
        public reportsService: ReportsService,
        public chartSetupSearchService: ChartSetupSearchService,
        protected currentUserStateService: CurrentUserStateService,
        public router: Router
    ) {
        super(coreDialogService, 'ChartSetup');
        this.chartSetupSearchService.setDefaultChartOptions(reportChartOptions);
        this.chartSetupSearchService.setResults(reportChartOptions);
        this.chartSetupSearchService.loadResults();
    }

    ngOnInit() {
        this.pageBuilderService.setDisplayChartEntityProfile(this.displayEntityProfile);
        this.pageBuilderService.setDisplaySourceAsLink(this.displaySourceAsLink);
        if (this.animationTrigger) {
            this.subscriptions.push(
                this.reportsService.getCurrentReport().subscribe(report => {
                    this.report = report;
                    if (report?.globalSourceSingleSeries) {
                        this.singleMentionSeries = [report.globalSourceSingleSeries.clone()];
                        this.singleMentionSeries.map(s => {
                            // we need to remove the series ID to the global series, or else the api will ignore it on a save
                            delete s.id;
                            // we need to delete these, otherwise, it would cause 500 error as the same searchCriteriaId would be accessed multiple times
                            s.resetSearchCriteriaId();
                        });
                    }
                    if (report?.globalSourceMultiSeries?.length > 0) {
                        this.multiMentionSeries = report.globalSourceMultiSeries.map(series =>  series.clone());
                        this.multiMentionSeries.map(s => {
                            // we need to remove the series ID to the global series, or else the api will ignore it on a save
                            delete s.id;
                            // we need to delete these, otherwise, it would cause 500 error as the same searchCriteriaId would be accessed multiple times
                            s.resetSearchCriteriaId();
                        });
                    }
                }),
                this.pageBuilderService.getSelectedBlock().subscribe(
                    (block) => {
                        if (block === this.blockClone) {
                            return;
                        }

                        this.isAdding = !this.pageBuilderService.blockExists(block);
                        this.originBlock = block;
                        this.lastSelectedMetric = (block?.displayElement as ChartDisplayElementModel)?.metric;

                        if (block) {
                            this.blockClone = block.clone();
                            this.setBlockSize();
                            this.pageBuilderService.selectBlock(this.blockClone);
                        } else {
                            this.blockClone = new BlockModel();
                        }
                        this.setUpBlockSizeUpdate();
                    }
                ),
                this.pageBuilderService.getBlocks().subscribe(
                    (blocks) => {
                        this.updateChartTitle();
                        this.validateChart();
                    }
                ),
                this.chartSetupSearchService.getFilters().subscribe(allFilters => {
                    this.chartFilters = allFilters;
                }),
                combineLatest([
                    this.chartSetupSearchService.getResults(),
                    this.currentUserStateService.getPermissions()
                ]).subscribe(([charts, permissions]) => {
                    this.charts = charts.filter(option => {
                        // Check whether the user has permission to view this chart
                        const hasPermission = option.permissions.some(p => permissions.includes(p));
                        // Hide GA charts when the `hideDataConnectorCharts` flag is set
                        const hideGA = this.hideDataConnectorCharts && option.isGAChartType();
                        // Hide Mention charts when the `hideMentionCharts` flag is set
                        const hideMention = this.hideMentionCharts && option.isMentionChartType();
                        // Hide Coverage Metric charts when the `hideCoverageMetricChart` flag is set
                        const hideCoverageMetric = this.hideCoverageMetricChart && option.blockType.key === ReportBlockTypeEnum.COVERAGE_METRIC;

                        return hasPermission && !hideGA && !hideMention && !hideCoverageMetric;
                    });
                }),
            );
        }
        this.setDefaultTitles();
    }

    viewModeChanged(mode: ChartViewModesEnum): void {
        if (mode === this.selectedViewMode) {
            return;
        }

        this.selectedViewMode = mode;
        this.showGalleryView = (mode === ChartViewModesEnum.GALLERY);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.blockSizeUpdateSubscription?.unsubscribe();
        this.chartSetupSearchService.clearFilters();
    }

    close(overlayVisibility: boolean): void {
        if (!overlayVisibility) {
            const output = this.blockClone.clone();
            this.pageBuilderService.deselectBlock(true);
            this.closeDialog({
                block: this.chartChanged ? output : null
            });
        }
    }

    openChartSettings(val: boolean) {
        this.editState = val;
        if (this.editState) {
            this.setBlock(this.selectedChartType);
        } else {
            // Clear old chart data
            this.blockClone.displayElement = null;
        }
    }

    toggleAllFilters() {
        const anyFiltersSelected = this.anyFiltersSelected;
        this.chartFilterRefs.forEach(filterRef => {
            filterRef.anyOptionsSelected = anyFiltersSelected;
            filterRef.selectItems();
        });
        this.anyFiltersSelected = !anyFiltersSelected;
    }

    filterChanged() {
        this.anyFiltersSelected = this.chartFilterRefs.some(filterRef => filterRef.anyOptionsSelected);
        this.chartSetupSearchService.applyFilters();
    }

    selectChart(type: BlockTypeModel, event?: any) {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        this.selectedChartType = type.clone();
    }

    selectChartViewOption() {
        switch (this.selectedChartViewOption.value) {
            case ChartSetupViewOptionsEnum.showDescription:
                this.showDescription = true;
                break;
            case ChartSetupViewOptionsEnum.hideDescription:
                this.showDescription = false;
                break;
        }

        // update Chart Setup View Options
        this.chartViewOptions = [
            this.showDescription ? this.hideDescriptionOption : this.showDescriptionOption
        ];
        this.selectedChartViewOption = null;
    }

    validateChart(): void {
        if (!this.blockClone || !this.blockClone.type || !this.blockClone.displayElement) {
            this.chartIsValid = false;
            return;
        }

        const displayElement = this.blockClone.displayElement as ChartDisplayElementModel;

        this.chartIsValid = true;
        // verify title
        if (!displayElement.title || displayElement.title.trim().length === 0) {
            this.chartIsValid = false;
            return;
        }

        if (
            this.blockClone.type.key === ReportBlockTypeEnum.SENTIMENT_BREAKDOWN
            || this.blockClone.type.key === ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON
        ) {
            // verify atleast one sentiment option is selected; this check should happen before checking series
            if (displayElement && displayElement.sentiments && displayElement.sentiments.length === 0) {
                this.chartIsValid = false;
                return;
            }
        }

        // verify, in the coverage summary case, that atleast one option is selected
        if (this.blockClone.type.key === ReportBlockTypeEnum.COVERAGE_SUMMARY) {
            if (displayElement.metrics?.length === 0) {
                this.chartIsValid = false;
                return;
            }
        }

        if (
            this.blockClone.type.key === ReportBlockTypeEnum.COVERAGE_IMPACT_TREND
            || this.blockClone.type.key === ReportBlockTypeEnum.REFERRAL_TREND
        ) {
            if (displayElement.referralTypes.length === 0) {
                this.chartIsValid = false;
                return;
            }
        }

        if ((this.blockClone.type.key === ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK) ||
            (this.blockClone.type.key === ReportBlockTypeEnum.TOP_POSTS)) {
            if (displayElement.networkSelection?.length === 0) {
                this.chartIsValid = false;
                return;
            }
        }

        if ((this.blockClone.type.key === ReportBlockTypeEnum.TOP_POSTS) ||
            (this.blockClone.type.key === ReportBlockTypeEnum.TOP_MENTIONS)) {
            if (displayElement?.topMentionsSortField === SearchCriteriaSortFieldEnum.custom) {
                displayElement.series[0].styles.ignoreApiCall = true;
                displayElement.dateRangeLabel = null;
            } else {
                displayElement.series[0].styles.ignoreApiCall = false;
            }
        } else {
            displayElement.series[0].styles.ignoreApiCall = false;
        }

        // verify a series exists
        if (displayElement.series.length < 1) {
            this.chartIsValid = false;
            return;
        }

        // verify external data series is valid
        const externalDataSeries = displayElement.getExternalDataSeries();
        if (externalDataSeries && externalDataSeries.length > 0) {
            externalDataSeries.forEach(series => {
                if (!series.hasValidExternalSource()) {
                    this.chartIsValid = false;
                    return;
                }
            });
        }

        // verify valid geography selection for map chart
        if (displayElement && displayElement.styles && displayElement.styles.chartGeographyType) {
            if (
                displayElement.styles.chartGeographyType !== GeographyTypesEnum.world
                && !displayElement.styles.regionIsoCode
            ) {
                this.chartIsValid = false;
                return;
            }
        }

        // verify series are valid (includes date range)
        const seriesAreValid = displayElement.areAllSourcesValidForChartType(this.blockClone.type.key as ReportBlockTypeEnum);
        if (!seriesAreValid) {
            this.chartIsValid = false;
            return;
        }

        if (this.blockClone.type.key === ReportBlockTypeEnum.PROMINENCE_BY_REACH
            || this.blockClone.type.key === ReportBlockTypeEnum.PROMINENCE_OVER_TIME) {
            if (!displayElement.series[0].prominenceTrendGroup?.id) {
                this.chartIsValid = false;
                return;
            }
        }

        // verify pr events
        if (displayElement?.prevents?.length > 0) {
            const noDateEvents = displayElement.prevents.filter(_event => !_event.date.absoluteDate);
            if (noDateEvents.length > 0){
                this.chartIsValid = false;
                return;
            }
        }
    }

    /**
     * Sets the preview block size so it will display it's contents at the right size
     */
    setBlockSize(): void {
        if (!this.blockClone?.type?.key) {
            return;
        }

        const gridSettings = this.pageBuilderService.gridSettings;
        let cols = reportChartInfoMap.get(this.blockClone.type.key as ReportBlockTypeEnum).dimensions.width;
        const rows = reportChartInfoMap.get(this.blockClone.type.key as ReportBlockTypeEnum).dimensions.height;

        if (this.blockClone.type.key === ReportBlockTypeEnum.TOP_10_OUTLETS || this.blockClone.type.key === ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME) {
            if ((this.blockClone.displayElement as ChartDisplayElementModel).chartHeaderSettings.entityProfile) {
                cols = 5;
            }
        }

        this.blockWidth = cols * gridSettings.fixedColWidth + (cols - 1) * gridSettings.margin;
        this.blockHeight = rows * gridSettings.fixedRowHeight + (rows - 1) * gridSettings.margin;
    }

    /**
     * Checks to see if a calendar picker is currently open
     * @HACK this helps resolve an issue where if the date picker is open when saving the result of the dialog,
     *       then the date range does not get updated properly
     */
    isDatePickerOpen(): boolean {
        const picker: HTMLElement[] = Array.from(document.querySelectorAll('.ui-datepicker'));
        const isOpen = picker.find((element) => {
            return element.style.display !== 'none';
        });
        return !!isOpen;
    }

    /**
    * Adds the new chart to page builder
    */
    addChart(): void {
        // @HACK Fixes a bug that if you click "add chart" while the date filter calendar is open, then the dates you picked will not be selected
        if (this.isDatePickerOpen()) {
            requestAnimationFrame(() => {
                this.addChart();
            });
            return;
        }

        this.compareWithGlobalSeries();

        const displayElement = this.blockClone.displayElement as ChartDisplayElementModel;
        displayElement.series.forEach(_series => {
            // mapping numOfItems and termType to series, which will be used to construct BE request
            _series.termType = displayElement.termType;
            _series.numOfItems = displayElement.numOfItems;
            _series.seriesUniqueUuid = uuid.v4();
        });
        this.blockClone.canAnnotate = annotatableBlockTypes.includes(this.blockClone.type.key as ReportBlockTypeEnum);

        this.pageBuilderService.addBlock(this.blockClone);
        this.chartChanged = true;
        this.hideSlideOverlay = true;
    }

    /**
    * Saves the changes back to the chart
    */
    updateChart(): void {
        // @HACK Fixes a bug that if you click "update chart" while the date filter calendar is open, then the dates you picked will not be selected
        if (this.isDatePickerOpen()) {
            requestAnimationFrame(() => {
                this.updateChart();
            });
            return;
        }

        this.compareWithGlobalSeries();

        const displayElement = this.blockClone.displayElement as ChartDisplayElementModel;
        displayElement.series.forEach(_series => {
            // BehaviorSubject is used for dataProvider
            // so here clear series data so that old data would not be emitted to chart when updating chart
            _series.clearData();

            // mapping numOfItems and termType to series, which will be used to construct BE request
            _series.termType = displayElement.termType;
            _series.numOfItems = displayElement.numOfItems;
        });
        this.blockClone.canAnnotate = annotatableBlockTypes.includes(this.blockClone.type.key as ReportBlockTypeEnum);
        this.handlePropertySync();

        // need to replace to make it handle the resize correctly
        this.pageBuilderService.replaceBlock(this.originBlock, this.blockClone);
        this.chartChanged = true;
        this.hideSlideOverlay = true;
    }

    /**
     * compare the current series config with with global source
     * if not equal, remove series id and some search criteria config
     */
    compareWithGlobalSeries(): void {
        let isUsingGlobalSeries = false;
        const sourceType = reportChartInfoMap.get(this.blockClone.type.key as ReportBlockTypeEnum).mentions;
        const series = (this.blockClone.displayElement as ChartDisplayElementModel).series;

        if (sourceType === ReportChartSourcesTypeEnum.single) {
            if (this.report?.globalSourceSingleSeries) {
                isUsingGlobalSeries = series[0].isEqual(this.report.globalSourceSingleSeries, true);
            }
        } else if (sourceType === ReportChartSourcesTypeEnum.multi) {
            if (this.report?.globalSourceMultiSeries?.length === series.length) {
                isUsingGlobalSeries = true;
                for (let index = 0; index < series.length; index++) {
                    if (!series[index].isEqual(this.report.globalSourceMultiSeries[index], true)) {
                        isUsingGlobalSeries = false;
                    }
                }
            }
        }

        if (!isUsingGlobalSeries) {
            series.map(seriesItem => {
                seriesItem.id = null;
                seriesItem.mentions.searchCriteriaId = null;
                if (seriesItem.mentions.filters) {
                    seriesItem.mentions.filters.id = null;
                    seriesItem.mentions.filters.name = null;
                }
            });
        }
    }

    handlePropertySync(): void {
        if (this.blockClone.type.key === ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS) {
            const gaMetrixToReportTypesMap = new Map<ChartMetricsEnum, GoogleAnalyticsReportRequestTypeEnum>([
                [ChartMetricsEnum.sessions, GoogleAnalyticsReportRequestTypeEnum.TOP_SOCIAL_REFERRERS_SESSIONS],
                [ChartMetricsEnum.users, GoogleAnalyticsReportRequestTypeEnum.TOP_SOCIAL_REFERRERS_USERS]
            ]);
            const displayElement = this.blockClone.displayElement as ChartDisplayElementModel;
            if (gaMetrixToReportTypesMap.get(displayElement.series[0].metric)
                !== displayElement.series[0].externalData.googleAnalyticsReportType
            ) {
                displayElement.series[0].externalData.googleAnalyticsReportType = gaMetrixToReportTypesMap.get(displayElement.series[0].metric);
            }
        }
    }

    /**
         * Returns a new ReportSeriesModel[] with default values
         *
         * @returns ReportSeriesModel[]
         */
    getDefaultSeriesList(blockType: BlockTypeModel): ReportSeriesModel[] {
        return [new ReportSeriesModel(null, blockType.key as ReportBlockTypeEnum)];
    }

    getSeriesForChart(info: ReportChartInfo, type: BlockTypeModel): ReportSeriesModel[] {
        const series = [];

        // @HACK PROMINENCE_BY_REACH and PROMINENCE_OVER_TIME charts are defined as ReportChartSourcesTypeEnum.multi to use multi-X-Y chart
        // But they actually are use single mention source
        if (
            info.mentions === ReportChartSourcesTypeEnum.single
            || type.key === ReportBlockTypeEnum.PROMINENCE_BY_REACH
            || type.key === ReportBlockTypeEnum.PROMINENCE_OVER_TIME
        ) {
            if (this.singleMentionSeries.length === 0 && this.multiMentionSeries.length > 0) {
                this.singleMentionSeries = this.multiMentionSeries.slice(0, 1);
            }

            series.push(...this.singleMentionSeries);
        } else if (
            info.mentions === ReportChartSourcesTypeEnum.multi
            && type.key !== ReportBlockTypeEnum.PROMINENCE_BY_REACH
            && type.key !== ReportBlockTypeEnum.PROMINENCE_OVER_TIME
        ) {
            if (this.multiMentionSeries.length === 0 && this.singleMentionSeries.length > 0) {
                this.multiMentionSeries = this.singleMentionSeries.slice(0, 1);
            }

            series.push(...this.multiMentionSeries);
        }

        if (info.externalData === ReportChartSourcesTypeEnum.single) {
            if (this.singleExternalSeries.length === 0 && this.multiExternalSeries.length > 0) {
                this.singleExternalSeries = this.multiExternalSeries.slice(0, 1);
            } else if (this.singleExternalSeries.length === 0 && info.googleAnalyticsReportType === GoogleAnalyticsReportRequestTypeEnum.REFERRAL_TREND) {
                this.singleExternalSeries = [new ReportSeriesModel(null, ReportBlockTypeEnum.REFERRAL_TREND, SeriesTypeEnum.googleAnalytics)];
            }

            series.push(...this.singleExternalSeries);
        } else if (info.externalData === ReportChartSourcesTypeEnum.multi) {
            if (this.multiExternalSeries.length === 0 && this.singleExternalSeries.length > 0) {
                this.multiExternalSeries = this.singleExternalSeries.slice(0, 1);
            }

            series.push(...this.multiExternalSeries);
        }

        return series;
    }

    setBlock(type: BlockTypeModel) {
        const oldType = this.blockClone.type;
        this.blockClone.type = type;

        if (this.blockClone.displayElement && oldType) {
            const oldInfo = reportChartInfoMap.get(oldType.key as ReportBlockTypeEnum);
            this.saveChartSeries(this.blockClone.displayElement as ChartDisplayElementModel, oldInfo);
        }

        const newInfo = type ? reportChartInfoMap.get(type.key as ReportBlockTypeEnum) : null;

        if (!this.blockClone.displayElement) {
            this.blockClone.displayElement = new type.displayElementType();
            const newDisplayElement = this.blockClone.displayElement as ChartDisplayElementModel;
            newDisplayElement.chartSpecificType =
                reportBlockTypeEnumToDefaultChartSpecificType.get(type.key as ReportBlockTypeEnum);
            newDisplayElement.chartHeaderSettings.displaySourceAsLink = this.displaySourceAsLink;
            newDisplayElement.series = this.getSeriesForChart(newInfo, type);
            newDisplayElement.series.map(series => series.chartType = type.key as ReportBlockTypeEnum);
            for (const series of newDisplayElement.series) {
                series.chartType = type.key as ReportBlockTypeEnum;
                if (this.globalDateQuery) {
                    series.dateRange = new ReportDateRangeModel().setFromDateQuery(this.globalDateQuery);
                }
            }
            if (this.hideMetadataContainer) {
                const headerSettings = newDisplayElement.chartHeaderSettings;
                headerSettings.title = false;
                headerSettings.dateRange = false;
                headerSettings.sourceLink = false;
            }
            if (this.accentColor) {
                newDisplayElement.styles.colorMap['accentColor'] = this.accentColor;
                const accentColorMapKeys  = reportBlockTypesToSetupDialogAccentColorMapKeysMap.get(type.key as ReportBlockTypeEnum);
                if (accentColorMapKeys) {
                    accentColorMapKeys.forEach(x => newDisplayElement.styles.colorMap[x] = this.accentColor);
                }
            }
        } else {
            // TODO: Figure out if still needed. Setting displayElement = null in openChartSettings might remove this branch
            const oldDisplayElement = this.blockClone.displayElement as ChartDisplayElementModel;

            this.blockClone.displayElement = new type.displayElementType();

            // copy properties from the old one, they can always be ignored
            const keys = Object.keys(oldDisplayElement);
            keys.forEach((key) => {
                if (key === 'styles') {
                    this.blockClone.displayElement.styles = oldDisplayElement.styles.clone();
                } else if (key === '_series') {
                    // skip series, it's handled separately
                    return;
                } else {
                    this.blockClone.displayElement[key] = oldDisplayElement[key];
                }
            });
        }
        const chartDisplayElement = this.blockClone.displayElement as ChartDisplayElementModel;
        const { dimensions, dynamicTitle } = reportChartInfoMap.get(type.key as ReportBlockTypeEnum);
        chartDisplayElement.dynamicTitle = dynamicTitle;
        chartDisplayElement.timeInterval = defaultChartTypesToTimeIntervalsMap.get(type.key as ReportBlockTypeEnum);

        const isTopMentionsOrTopPosts = [
            ReportBlockTypeEnum.TOP_MENTIONS,
            ReportBlockTypeEnum.TOP_POSTS
        ].includes(this.selectedChartType?.key as ReportBlockTypeEnum);

        let defaultMetric: ChartMetricsEnum | SearchCriteriaSortFieldEnum = isTopMentionsOrTopPosts
            ? SearchCriteriaSortFieldEnum.audience_reach
            : ChartMetrics.volumeOfMentions;

        /**
         * If one, and only one, data type is selected in the Data Types filter, we set that metric as the default for the selected chart.
         * If no data type or more than one data type is selected, we default to "volume", since we can’t determine which metric the user intends to use.
        */
        const selectedDataTypes = this.chartFilters.find(filter => filter.id === ChartSetupFiltersEnum.dataTypes)?.getValue();
        if (selectedDataTypes?.length === 1) {
            const selectedDataType = selectedDataTypes[0];
            defaultMetric = isTopMentionsOrTopPosts
                ? ChartDataTypesToSearchCriteriaSortFieldMap.get(selectedDataType) ?? SearchCriteriaSortFieldEnum.audience_reach
                : ChartDataTypesToChartMetricsMap.get(selectedDataType) ?? ChartMetrics.volumeOfMentions;
        }

        if (this.selectedChartType?.key === ReportBlockTypeEnum.COVERAGE_OVER_TIME || this.selectedChartType?.key === ReportBlockTypeEnum.COVERAGE_SUMMARY) {
            chartDisplayElement.metrics = (chartDisplayElement.metrics || [ defaultMetric ]) as ChartMetricsEnum[];
        } else {
            chartDisplayElement.metric = defaultMetric as ChartMetricsEnum;
        }

        if (newInfo) {
            chartDisplayElement.series = this.getSeriesForChart(newInfo, type).map(
                series => series.clone(type.key as ReportBlockTypeEnum)
            );
            for (const series of chartDisplayElement.series) {
                if (this.globalDateQuery) {
                    series.dateRange = new ReportDateRangeModel().setFromDateQuery(this.globalDateQuery);
                }
            };
        }

        chartDisplayElement.size = dimensions;
        if (!oldType) {
            this.blockClone.rows = dimensions.height;
            this.blockClone.cols = dimensions.width;
        }

        if (!chartDisplayElement.series || chartDisplayElement.series.length === 0) {
            chartDisplayElement.series = [new ReportSeriesModel(null, type.key as ReportBlockTypeEnum)];

            // used in briefings charts, to add default date
            if (this.globalDateQuery) {
                for (const series of chartDisplayElement.series) {
                    series.dateRange = new ReportDateRangeModel().setFromDateQuery(this.globalDateQuery);
                }
            };
        }

        if (oldType && oldType.key !== type.key && (oldType.key === geographyBreakdownBlockType.key || type.key === geographyBreakdownBlockType.key)) {
            // remove region filters when switching in and out of geography breakdowns
            chartDisplayElement.series.forEach(seriesItem => {
                if (seriesItem.mentions.filters) {
                    seriesItem.mentions.filters.searchCriteria.query.includedRegions = [];
                }
            });
        }

        switch (type.key) {
            case referralTrendBlockType.key:
                chartDisplayElement.referralTypes = [
                    GoogleAnalyticsReportTypesEnum.TOTAL_REFERRALS,
                    GoogleAnalyticsReportTypesEnum.WEB_REFERRALS,
                    GoogleAnalyticsReportTypesEnum.SOCIAL_REFERRALS,
                    GoogleAnalyticsReportTypesEnum.EMAIL_REFERRALS
                ];
                break;
            case coverageImpactTrendBlockType.key:
                chartDisplayElement.referralTypes = [
                    GoogleAnalyticsReportTypesEnum.UNIQUE_PAGEVIEWS,
                    GoogleAnalyticsReportTypesEnum.TOTAL_PAGEVIEWS,
                    GoogleAnalyticsReportTypesEnum.SESSIONS,
                    GoogleAnalyticsReportTypesEnum.USERS
                ];
                break;
            case sentimentBreakdownBlockType.key:
            case sentimentBreakdownComparisonBlockType.key:
            case sentimentTrendBlockType.key:
                chartDisplayElement.sentiments = [
                    SearchCriteriaQuerySentimentsEnum.positive,
                    SearchCriteriaQuerySentimentsEnum.neutral,
                    SearchCriteriaQuerySentimentsEnum.negative,
                    SearchCriteriaQuerySentimentsEnum.notAvailable
                ];
                break;
            case topMentionsBlockType.key:
                // there might be a better place for this initalization logic, but I cannot find one
                chartDisplayElement.topMentionsSortField = defaultMetric as SearchCriteriaSortFieldEnum;
                const topMentionsMetadata = new MentionCustomizationModel();
                topMentionsMetadata.mentionContent = MentionContentCustomizationEnum.lead;
                topMentionsMetadata.displayInsightsName = true;
                topMentionsMetadata.insightsNameDisplay = InsightsDisplayNameEnum.insightsName;
                topMentionsMetadata.metadataTypes.push(
                    MentionMetadataTypesEnum.audienceReach, MentionMetadataTypesEnum.ave, MentionMetadataTypesEnum.domainAuthority
                );
                chartDisplayElement.topMentionsMetadata = topMentionsMetadata;
                chartDisplayElement.topCuratedMentions = [];
                break;
            case topPostsBlockType.key:
                // there might be a better place for this initalization logic, but I cannot find one
                chartDisplayElement.topMentionsSortField = defaultMetric as SearchCriteriaSortFieldEnum;
                chartDisplayElement.topCuratedMentions = [];
                break;
            case coverageMetricBlockType.key:
                const coverageMetricChartDisplayElement = chartDisplayElement as CoverageMetricChartDisplayElement;
                const titleIcon = coverageMetricChartDisplayElement.titleIcon;
                if (!titleIcon) {
                    (chartDisplayElement as CoverageMetricChartDisplayElement).titleIcon = (chartDisplayElement as CoverageMetricChartDisplayElement).metric;
                }
                coverageMetricChartDisplayElement.chartHeaderSettings.icon = true;
                coverageMetricChartDisplayElement.chartHeaderSettings.title = true;
                coverageMetricChartDisplayElement.chartHeaderSettings.sparkChart = true;
                break;
        }

        // we want to set the default series source type to theme instead of folder
        for (const series of chartDisplayElement.series) {
            if (series.chartType === ReportBlockTypeEnum.TOP_POSTS && !series.hasValidSavedMentionSource() && !series.hasValidMentionsSource()) {
                series.type =  SeriesTypeEnum.theme;
            }

            if (series.chartType === ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS
                || series.chartType === ReportBlockTypeEnum.TOP_REFERRAL_SOURCES
                || series.chartType === ReportBlockTypeEnum.REFERRAL_TREND
            ) {
                series.type =  SeriesTypeEnum.googleAnalytics;
            }
        }

        this.setDefaultTitles();
        this.updateChartTitle();

        if (this.overrideBlockTypeFields) {
            this.overrideBlockTypeFields(type);
        }

        if (oldType && type.componentType !== oldType.componentType) {
            this.blockClone = this.blockClone.clone();
        }

        this.setBlockSize();
        this.validateChart();
        this.setUpBlockSizeUpdate();

        this.pageBuilderService.selectBlock(this.blockClone);
    }

    saveChartSeries(displayElement: ChartDisplayElementModel, info: ReportChartInfo) {
        if (info.mentions === ReportChartSourcesTypeEnum.single) {
            this.singleMentionSeries = displayElement.getMentionSeries();
        } else if (info.mentions === ReportChartSourcesTypeEnum.multi) {
            this.multiMentionSeries = displayElement.getMentionSeries();
        }

        if (info.externalData === ReportChartSourcesTypeEnum.single) {
            this.singleExternalSeries = displayElement.getExternalDataSeries();
        } else if (info.externalData === ReportChartSourcesTypeEnum.multi) {
            this.multiExternalSeries = displayElement.getExternalDataSeries();
        }
    }

    updateChartTitle(): void {
        const displayElement = this.blockClone.displayElement as ChartDisplayElementModel;
        if (!displayElement || displayElement.title !== this.oldTitle) {
            this.oldTitle = displayElement?.title;
            return;
        }
        let title = '';
        if (displayElement.dynamicTitle) {
            title = this.getDynamicTitle(displayElement);
        } else {
            title = this.localizationService.transform('Reports.chart.title.' + this.blockClone.type.key);
        }
        if (displayElement.title) {
            if (this.isChartTitleDefault()) {
                displayElement.title = title;
            }
        } else {
            displayElement.title = title;
        }
        this.oldTitle = displayElement.title;
        const field = this.blockFormComponent?.getFieldComponentByProperty('title');
        // This depends on name change not re-triggering ngOnChanges
        if (field) {
            field.value = displayElement.title;
        }
    }

    getDynamicTitle(displayElement: ChartDisplayElementModel): string {
        let structure = this.localizationService.transform(`Reports.chart.title.${this.blockClone.type.key}.structure`);
        if (this.blockClone.type.key === ReportBlockTypeEnum.COVERAGE_OVER_TIME && displayElement.metrics.length > 1) {
            structure = this.localizationService.transform(`Reports.chart.title.${this.blockClone.type.key}.structure.multiMetrics`);
        }
        if (this.blockClone.type.key === ReportBlockTypeEnum.COVERAGE_METRIC &&
            (displayElement.metric === ChartMetricsEnum.IMPRESSIONS || displayElement.metric === ChartMetricsEnum.ENGAGEMENTS)
        ) {
            structure = this.localizationService.transform(`Reports.chart.title.${this.blockClone.type.key}.structure.plural`);
        }
        return this.reportsService.createDynamicChartTitle(
            structure,
            this.blockClone.displayElement
        );
    }

    setDefaultTitles(): void {
        this.defaultTitles = [];
        if (!(this.blockClone.displayElement as ChartDisplayElementModel)?.dynamicTitle) {
            return;
        }
        const dynamicTitleStructure = this.localizationService.transform(`Reports.chart.title.${this.blockClone.type.key}.structure`);
        this.addToDefaultTitlesByStructure(dynamicTitleStructure);
        if (this.blockClone.type.key === ReportBlockTypeEnum.COVERAGE_OVER_TIME) {
            this.addToDefaultTitlesByStructure(this.localizationService.transform(`Reports.chart.title.${this.blockClone.type.key}.structure.multiMetrics`));
        }
        if (this.blockClone.type.key === ReportBlockTypeEnum.COVERAGE_METRIC) {
            this.addToDefaultTitlesByStructure(this.localizationService.transform(`Reports.chart.title.${this.blockClone.type.key}.structure.plural`));
        }
    }

    addToDefaultTitlesByStructure(structure: string): void {
        const propertiesToEnumValuesMap: Map<string, string[]> = new Map<string, string[]>([
            [ 'metric', Object.values(ChartMetricsEnum) ],
            [ 'metrics', Object.values(ChartMetricsEnum) ],
            [ 'termType', Object.values(TermTypesEnum) ],
            [ 'timeInterval', Object.values(ChartTimeIntervalsEnum) ],
            [ 'topMentionsSortField', Object.values(SearchCriteriaSortFieldEnum) ]
        ]);
        const dynamicTitleParts: string[] = structure.match(/\[\[([A-Za-z\.]+)\]\]/g)?.map(x => x.replace(/[\[\]]/g, '').split('.')[0]);
        const dynamicTitlePartCandidates: string[][] = dynamicTitleParts.map(x => propertiesToEnumValuesMap.get(x));
        const allCombinationPropertyObjects = dynamicTitlePartCandidates.reduce((acc, curr, index) => {
            const combinations = [];
            acc.forEach(a => {
                curr.forEach(b => {
                    const c = JSON.parse(JSON.stringify(a));
                    c[dynamicTitleParts[index]] = b;
                    combinations.push(c);
                });
            });
            return combinations;
        }, [{} as { [key: string]: string }]);
        allCombinationPropertyObjects.forEach(x => this.defaultTitles.push(this.reportsService.createDynamicChartTitle(structure, x)));
    }

    isChartTitleDefault(): boolean {
        return this.defaultTitles.includes((this.blockClone.displayElement as ChartDisplayElementModel).title);
    }

    setUpBlockSizeUpdate(): void {
        if (this.blockSizeUpdateSubscription) {
            this.blockSizeUpdateSubscription.unsubscribe();
            this.blockSizeUpdateSubscription = null;
        }
        this.blockSizeUpdateSubscription = this.blockClone.componentChange$.subscribe(change => {
            this.setBlockSize();
        });
    }
}
